import React from "react";
import ReactHighcharts from "react-highcharts";
import { NoDataToDisplay } from "react-highcharts-no-data-to-display";
import { Utils } from "../common/utils";

NoDataToDisplay(ReactHighcharts.Highcharts);

ReactHighcharts.Highcharts.setOptions({
  lang: {
    noData: RESX.GeneralLabels.NoDataAvailable
  }
});

interface Props {
  data: server.dto.GroupedShowTotals[];
  currency: string;
  seriesVisible: string[];
  setSeriesVisible: (setFunc: (old: string[]) => string[]) => void;
}

export const SalesChart = (props: Props) => {
  const getChartConfig = (series) => ({
    series,
    chart: {
      style: {
        fontFamily: "inherit"
      },
      type: "column",
      backgroundColor: "transparent"
    },
    plotOptions: {
      series: {
        events: {
          legendItemClick: function (event) {
            props.setSeriesVisible(old => {
              const name = event.target.name;
              if (old.includes(name)) {
                return old.filter(s => s !== name);
              }
              return [...old, name];
            });
          },
        },
      },
      column: {
        borderColor: "transparent",
        dataLabels: {
          style: {
            color: "var(--text-color-light)"
          }
        }
      },
    },
    legend: {
      itemStyle: {
        color: "var(--text-color-light)",
      },
      itemHoverStyle: {
        color: "var(--text-color)",
      },
      itemHiddenStyle: {
        color: "var(--text-color-lighter)",
      },
    },
    title: {
      text: ""
    },
    tooltip: {
      pointFormat: `<p>{series.name}<p><br/><p><b>${props.currency.toUpperCase()} {point.y:,.0f}</b></p>`
    },
    xAxis: {
      labels: {
        style: {
          color: "var(--text-color-light)"
        }
      },
      categories: props.data.map(d => d.label)
    },
    yAxis: {
      labels: {
        style: {
          color: "var(--text-color-light)"
        }
      },
      title: {
        style: {
          color: "var(--text-color-light)"
        },
        enabled: true,
        rotation: 0,
        text: props.currency.toUpperCase()
      }
    },
    credits: {
      enabled: false
    }
  });

  const getSeries = () => {
    const allSalesProductTypes = props.data
      .map(d => d.salesSum)
      .reduce((acc, curr) => acc.concat(curr), []); // map+reduce is like doing SelectMany in C#
    const uniqueProductTypes = Utils.uniqBy(allSalesProductTypes, p => p.productType.value);
    const productValueMap: Record<string, { color: string, data: Array<{ name: string, y: number, color?: string }> }> = uniqueProductTypes.reduce((acc, curr) => ({
      ...acc,
      [curr.productType.text]: { data: [], color: curr.productType.hexColor }
    }), {});

    props.data.forEach(totals => {
      for (const prod of totals.salesSum) {
        productValueMap[prod.productType.text].data.push({ name: totals.label, y: prod.amount });
      }
    });

    return Object.keys(productValueMap)
      .map(key => ({
        data: productValueMap[key].data,
        name: key,
        minPointLength: 4,
        color: productValueMap[key].color
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const afterRender = (chart) => {
    chart.series.forEach(series => {
      if (!props.seriesVisible.includes(series.name)) {
        series.hide();
      }
    });
  };

  return <div>
    <ReactHighcharts isPureConfig callback={afterRender} config={getChartConfig(getSeries())} />
  </div>;
}
